import React, { useState, useEffect, useMemo } from 'react';
import {
  Radio,
  RadioGroup,
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  FormControlLabel,
  FormControl,
  Select,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from '@mui/material/Modal';
import { styled } from '@mui/material/styles';
import { FormInputTextArea } from '../../../form-components/FormInputTextArea';
import { useForm, Controller } from 'react-hook-form';
import { Close } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useToast } from 'use-toast-mui';
import { errorMessages, matchKorean, getChosung } from '../../../utils/methods';
import { FormInputText } from '../../../form-components/FormInputText';
import { FormInputComma } from '../../../form-components/FormInputComma';
import { createProductCancel, createProductRefund, getOrderPaymentLines, getTicketChangeInfo } from '../../../api/payment';
import { getUserPaymentDetail } from '../../../api/user';
import { getBranchManager } from '../../../api/staff';
import { getAllTickets, searchTickets } from '../../../api/ticket';
import { getAllPass } from '../../../api/ticket';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import CustomRadioBox from '../../CustomRadioBox';
import QueryString from 'qs';

// ManualModalV2와 동일한 결제수단 관련 컴포넌트들 import
import {
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconWrapper,
  ModalContent,
  ModalFooter,
  TabContainer,
  TabButton,
  FormRow,
  FormLabel,
  FormInputWrapper,
  FormRowMemo,
  textInputStyle,
  disabledInputStyle,
  dropdownStyle,
  dateInputStyle,
  dateInputStyleFlex,
  CancelButton,
  SaveButton,
  FormFieldLabel,
  FormFieldRow,
  FormFieldGroup,
  FormFieldGroupHalf,
  ContentSection,
  PointHintCaption,
  PointMinUseHint,
  DropdownArrowIcon,
} from '../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModalStyled';
import CustomTimePicker from '../payment/CustomTimePicker';
import { FormInputDate } from '../../../form-components/FormInputDate';
import moment from 'moment';
import usePointUsage from '../../SalesManagment/modal/enterSaleInfoModal/usePointUsage';
import CardPaymentForm from '../payment/cardPaymentForm';
import CashPaymentForm from '../payment/cashPaymentForm';
import SellerShareSelect, { buildSellerPayload, isSellerShareValid } from '../../common/SellerShareSelect';
import BankTransferForm from '../payment/bankPaymentForm';
import SplitPaymentSection, { SPLIT_METHOD_LABEL } from '../../SalesManagment/modal/enterSaleInfoModal/SplitPaymentSection';
import PaymentConfirmModal from '../../SalesManagment/modal/enterSaleInfoModal/PaymentConfirmModal';

// 환불/취소 탭 포인트 정산 미리보기 박스 스타일 (판매등록 힌트 톤과 통일)
const pointPreviewBoxStyle = {
  mt: 0,
  mb: '16px',
  p: '10px 12px',
  borderRadius: '8px',
  background: '#F7F8F8',
  border: '1px solid #E5E7E7',
};
const pointPreviewTitleStyle = { fontSize: '12px', fontWeight: 700, color: '#555', mb: '4px' };
const pointPreviewRowStyle = { fontSize: '12px', color: '#777', lineHeight: '20px' };

// 피그마(6518-9706) 하단 버튼 — 환불처리는 흰 배경 + 빨간 테두리·텍스트(위험 액션 아웃라인)
const DangerOutlineButton = styled('button')({
  minWidth: '200px',
  height: '40px',
  padding: '0 24px',
  borderRadius: '8px',
  border: '1px solid #E05B5B',
  backgroundColor: '#fff',
  color: '#E05B5B',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#FDF3F1' },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#fff' },
});

// ManualModalV2와 동일한 결제수단 옵션
const payMethods = [
  { value: '카드', label: '카드' },
  { value: '계좌이체', label: '계좌이체' },
  { value: '현금', label: '현금' },
];

// 수강권 구분 옵션
const ticketTypeRadioOptions = [
  { label: '수강권', value: 'TICKET' },
  { label: '이용권', value: 'PASS' },
];

const ManualRefundModalV2 = ({ fetchData, ticketData, open, onClose }) => {
  // modalStyle 삭제 — ModalContainer 공용 컴포넌트 사용

  const memberOrderId = ticketData?.memberOrderId;
  const paymentAmount = ticketData?.finalPrice;
  const outstandingAmount = ticketData?.outstandingAmount;
  const courseTicketName = ticketData?.courseTicketName;
  const ticketId = ticketData?.ticketId;
  const usedCount = ticketData?.usedCount;
  const lastPaidMemberOrderId = ticketData?.lastPaidMemberOrderId;
  // 구독 이용권 주문은 회원권 변경 불가(2026-10-12 사용자 결정) — change-info 의 subscription 으로 판정(아래에서 합친다).
  const isChangeDisabledByRow = ticketData?.isChangeDisabled;
  // 양수금(직접양도 수수료) 행은 미수금 결제 / 판매취소만 허용한다.
  // 판매취소 시 백엔드가 양도 자체를 당일 한정 롤백(원본 권 복원 + 양수자 권 만료)한다.
  const isTransferFee = ticketData?.payType === 'TRANSFER_FEE';

  const [searchInput, setSearchInput] = useState('');
  const toast = useToast();
  const { user } = useSelector(state => state.user);
  const { t } = useTranslation();

  const [usageCount, setUsageCount] = useState(0);
  const [ticketPrice, setTicketPrice] = useState(0);
  const [ticketDiscountAmount, setTicketDiscountAmount] = useState(0);
  const [ticketFinalPrice, setTicketFinalPrice] = useState(0);
  const [ticketOutstandingPrice, setTicketOutstandingPrice] = useState(0);
  const [ticketPassPrice, setTicketPassPrice] = useState(0);
  const [ticketPassDiscountAmount, setTicketPassDiscountAmount] = useState(0);
  const [ticketPassFinalPrice, setTicketPassFinalPrice] = useState(0);
  const [ticketPassOutstandingPrice, setTicketPassOutstandingPrice] = useState(0);
  const [selectedTicketType, setSelectedTicketType] = useState(null);

  // useForm defaultValues에 ManualModalV2와 동일한 결제수단 관련 필드들 추가
  const {
    handleSubmit,
    control,
    reset,
    setError,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      searchInput: '',
      refundAmount: '',
      useAmount: '',
      penaltyAmount: '',
      paymentAmount: '',
      outstandingAmount: '',
      branchManager: null,
      // #122 회원권 변경·미수금 수납이 만드는 새 주문의 판매담당자 N명 + 비율(환불·취소 담당자는 branchManager 단일)
      newOrderSellers: [],
      newOrderShares: {},
      memo: '',
      refundMethod: null,
      bankInfo: '',
      refundDepositorName: '',
      outstandingPaymentAmount: '',
      outstandingPaymentDate: '',
      outstandingPaymentTime: '',
      // ManualModalV2와 동일한 결제수단 관련 필드들
      paymentMethod: null,
      cardCompanyCode: null,
      cardNumber: '',
      approvalNumber: '',
      receiptYn: 'Y',
      deductionType: 0,
      receiptType: 'phone',
      personalId: '',
      businessNumber: '',
      depositorName: '',
      issuePhoneNumber: '',
      issueBusinessNumber: '',
      installmentMonth: null,
      // 변경 관련 필드들
      courseTicketNameNow: '',
      usedCountNow: '',
      paymentAmountNow: '',
      ticketType: 'TICKET',
      newTicket: null,
      newTicketPrice: '',
      newTicketDiscountAmount: '',
      newTicketFinalPrice: '',
      newTicketOutstandingPrice: '',
      newUsageCount: '',
      keepRemaining: '',
      changePaymentMethod: null,
      usedPoint: '',
    },
  });

  const [branchManager, setBranchManager] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(user.branch?.branchId ? user.branch.branchId : null);
  const [openDialog, setOpenDialog] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [refundType, setRefundType] = useState('refund');

  // 변경 관련 상태들
  const [availableTickets, setAvailableTickets] = useState([]);
  const [availablePasses, setAvailablePasses] = useState([]);

  // watch를 통해 실시간으로 값 변화 감지
  const watchUseAmount = watch('useAmount');
  const watchPenaltyAmount = watch('penaltyAmount');
  const watchPaymentAmount = watch('paymentAmount');
  const watchOutstandingAmount = watch('outstandingAmount');
  const watchRefundMethod = watch('refundMethod');
  // #49 수단별 환불 라인. 합계가 환불금액과 같아야 처리할 수 있다(서버 동일 검증).
  const [refundLines, setRefundLines] = useState([]);
  // 이 주문의 수납 라인(결제 시 수단별 기록) — 자동 배분 프리필의 근거
  const [orderPaymentLines, setOrderPaymentLines] = useState([]);
  const watchRefundAmountValue = Number(String(watch('refundAmount') || 0).replace(/,/g, '')) || 0;
  const refundLinesTotal = refundLines.reduce((sum, line) => sum + line.amount, 0);

  // 모달을 열면 수납 라인을 조회한다. 라인이 없는 주문(과거 데이터)은 빈 배열 → 프리필 없음.
  useEffect(() => {
    if (!open || !memberOrderId || !user?.token) return;
    getOrderPaymentLines(memberOrderId, user.token)
      .then(lines => setOrderPaymentLines(Array.isArray(lines) ? lines : []))
      .catch(() => setOrderPaymentLines([]));
  }, [open, memberOrderId, user?.token]);

  // 회원권 변경 — 기존 보유권 사용량·잔여가치(실결제 × 남은 비율). 실패하면 null(구주문 폴백).
  const [changeInfo, setChangeInfo] = useState(null);
  useEffect(() => {
    if (!open || !memberOrderId || !user?.token) return;
    setChangeInfo(null);
    getTicketChangeInfo(memberOrderId, user.token)
      .then(info => {
        const ok = info && typeof info === 'object' ? info : null;
        setChangeInfo(ok);
        // 사용횟수는 '사용 / 최대' 로 함께 표시한다. 기간제는 일수 기준.
        if (ok?.calcBasis === 'COUNT' && ok.usedCount != null) {
          setValue('usedCountNow', `${ok.usedCount} / ${ok.usageCount ?? '-'}`);
        } else if (ok?.calcBasis === 'PERIOD' && ok.usedDays != null) {
          setValue('usedCountNow', `${ok.usedDays}일 / ${ok.totalDays}일`);
        }
      })
      .catch(() => setChangeInfo(null));
  }, [open, memberOrderId, user?.token]);
  // 회당(일당) 금액 = 실결제액 ÷ 총횟수(총일수). 수기 수정 가능 — 잔여가치 자동계산이 이 값을 쓴다.
  const changeTotalUnits = changeInfo?.calcBasis === 'COUNT'
    ? Number(changeInfo.usageCount) || 0
    : changeInfo?.calcBasis === 'PERIOD'
      ? Number(changeInfo.totalDays) || 0
      : 0;
  const changeUsedUnits = changeInfo?.calcBasis === 'COUNT'
    ? Number(changeInfo.usedCount) || 0
    : changeInfo?.calcBasis === 'PERIOD'
      ? Number(changeInfo.usedDays) || 0
      : 0;
  // 자동계산 기준은 결제금액(실결제) — 정가 기준 아님 (사용자 결정 2026-08-12).
  const autoPerUnit = changeTotalUnits > 0
    ? Math.round(((Number(changeInfo?.paymentAmount) || 0) / changeTotalUnits) * 100) / 100
    : 0;
  const perUnitInputRaw = watch('changePerUnitInput');
  const changePerUnit = perUnitInputRaw !== undefined && perUnitInputRaw !== null && String(perUnitInputRaw) !== ''
    ? Number(String(perUnitInputRaw).replace(/,/g, '')) || 0
    : autoPerUnit;
  useEffect(() => {
    setValue('changePerUnitInput', changeTotalUnits > 0 ? String(autoPerUnit) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPerUnit, changeTotalUnits]);
  // 사용분 = 회당 금액 × 사용횟수(일수) — 회당 금액을 고치면 잔여가치도 같이 재계산된다.
  const changeUsedValue = changeTotalUnits > 0 ? Math.round(changePerUnit * changeUsedUnits) : 0;
  // 회당 금액 표시 — 정수부만 콤마, 소수부는 그대로 (실결제÷횟수가 소수일 수 있다).
  const formatPerUnitDisplay = v => {
    if (v === undefined || v === null || v === '') return '';
    const [intPart, decPart] = String(v).replace(/,/g, '').split('.');
    const withComma = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${withComma}.${decPart}` : withComma;
  };

  // 잔여가치. change-info 를 못 받으면 기존 방식(총결제액)으로 폴백해 화면이 죽지 않게 한다.
  const autoChangeCredit = changeInfo?.availableAmount != null
    ? changeTotalUnits > 0
      ? Math.max(0, Math.round(Number(changeInfo.paymentAmount) || 0) - changeUsedValue)
      : Number(changeInfo.availableAmount) || 0
    : Number(ticketData?.totalOrderPaymentAmount) || 0;
  // 잔여가치는 수기 수정 가능(사용자 결정) — 자동계산값을 기본으로 채우고, 이후 입력값이 우선한다.
  const changeCreditInputRaw = watch('changeCreditInput');
  const changeCredit = changeCreditInputRaw !== undefined && changeCreditInputRaw !== null && String(changeCreditInputRaw) !== ''
    ? Number(String(changeCreditInputRaw).replace(/,/g, '')) || 0
    : autoChangeCredit;
  useEffect(() => {
    setValue('changeCreditInput', String(autoChangeCredit));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoChangeCredit]);
  // 선택 가능 기준 — 새 상품 판매가 ≥ 잔여가치(실결제 × 남은 비율). 추가결제가 max(0, 판매가 − 잔여가치)라
  // 막아야 할 것은 환불이 생기는 변경뿐이다(사용자 결정 2026-09-17, 종전 "정가 상향 전용" 대체).
  // 서버 게이트(validateCoversCredit)는 서버가 계산한 availableAmount 를 쓰므로 수기 수정한 잔여가치(changeCredit)는
  // 판정에 쓰지 않는다 — 화면에서 고른 상품이 저장에서 거부되지 않아야 한다. 0 도 유효값이라 ?? 로 받는다.
  const changeCreditGate =
    changeInfo?.availableAmount != null
      ? Number(changeInfo.availableAmount)
      : Number(ticketData?.totalOrderPaymentAmount) || 0;
  // 듀엣 상품가는 2인 총액이라 1인분으로 환산해 서버(toPerMemberProductPrice)와 축을 맞춘다.
  const toPerMemberPrice = item =>
    (Number(item.finalPrice ?? item.regularPrice) || 0) / (item.classType === 'DUET_LESSON' ? 2 : 1);
  // 미수금이 남은 주문은 변경 불가 — BE 가드(미수금 먼저 정리 유도)와 같은 기준을 화면에서도 막는다.
  const changeBlockedByOutstanding =
    (Number(changeInfo?.outstandingAmount ?? ticketData?.outstandingAmount) || 0) > 0;

  // 회원권 변경/미수금 결제 — 판매등록과 동일한 결제 정보 입력 모달로 수단(분할)을 받는다.
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [paySplitLines, setPaySplitLines] = useState([]);

  // 환불/취소 시 포인트 정산 미리보기 근거 — 주문의 사용 포인트·구매 적립 포인트(orderdetail의 earnedPoint)
  const [orderPointInfo, setOrderPointInfo] = useState(null);
  useEffect(() => {
    if (!open || !memberOrderId || !user?.token) {
      setOrderPointInfo(null);
      return;
    }
    getUserPaymentDetail(memberOrderId, user.token)
      .then(data => setOrderPointInfo(data?.content?.[0] || null))
      .catch(() => setOrderPointInfo(null));
  }, [open, memberOrderId, user?.token]);

  // 자동 배분(2026-08-05 결정): 수납 라인 순서대로 환불금액을 소진해 기본값을 깔고, 직원이 수정한다.
  // 환불금액이 바뀌면(이용금액·위약금 조정) 다시 배분한다.
  useEffect(() => {
    if (watchRefundAmountValue <= 0) {
      setRefundLines([]);
      return;
    }
    let remaining = watchRefundAmountValue;
    const allocated = [];
    orderPaymentLines.forEach((line, index) => {
      const method = line.paymentMethod;
      if (remaining <= 0 || !['CARD', 'CASH', 'TRANSFER'].includes(method)) return;
      const take = Math.min(Number(line.amount) || 0, remaining);
      if (take <= 0) return;
      remaining -= take;
      allocated.push({
        key: `prefill-${line.id ?? index}`,
        method,
        amount: take,
        orderPaymentId: line.id ?? null,
        installmentMonth: line.installmentMonth || '00',
        cardCompanyCode: line.cardCompanyCode || '',
        cardNumber: line.cardNumber || '',
        cid: line.cid || line.cId || '',
        bankInfo: line.bankInfo || '',
        depositorName: line.depositorName || '',
      });
    });
    setRefundLines(allocated);
  }, [watchRefundAmountValue, orderPaymentLines]);
  const watchChangePaymentMethod = watch('changePaymentMethod');
  const watchTicketType = watch('ticketType');
  const watchNewTicket = watch('newTicket');
  const isSubscriptionOrder = changeInfo?.subscription === true;
  const isChangeDisabled = isChangeDisabledByRow || isSubscriptionOrder;
  // 구독 주문으로 열렸는데 기본 탭이 변경이면 환불로 돌린다.
  useEffect(() => {
    if (isSubscriptionOrder && refundType === 'change') setRefundType('refund');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriptionOrder]);

  // 남는 금액을 기존 권으로 남기기(2026-10-12 사용자 결정). 새 상품 판매가가 잔여가치보다 낮을 때,
  // 횟수제 수강권은 남길 횟수·기간제 이용권은 남길 일수를 직원이 입력한다(자동값 = 남는 금액 ÷ 회당·일당 금액, 내림).
  // 그 밖의 권(무제한 수강권·일일권)은 종전대로 잔여가치 미만 상품을 고를 수 없다.
  const keepUnit =
    changeInfo?.productType === 'TICKET' && changeInfo?.calcBasis === 'COUNT'
      ? 'COUNT'
      : changeInfo?.productType === 'PASS' && changeInfo?.calcBasis === 'PERIOD'
        ? 'DAYS'
        : null;
  const selectedNewItem = watchNewTicket
    ? (watchTicketType === 'TICKET' ? availableTickets : availablePasses).find(item => item.value === watchNewTicket)
    : null;
  const needsKeep = !!selectedNewItem && !!keepUnit && toPerMemberPrice(selectedNewItem) < changeCreditGate;
  // 남는 금액은 needsKeep 판정과 같은 서버 잔여가치(changeCreditGate) 기준 — 수기 수정한 잔여가치(changeCredit)로 계산하면
  // 남길 횟수가 필요하다고 판정해 놓고 남는 금액은 0으로 나오는 모순이 생긴다(리뷰 지적).
  const leftoverAmount = selectedNewItem ? Math.max(0, changeCreditGate - toPerMemberPrice(selectedNewItem)) : 0;
  // 남길 수 있는 상한 = 지금 남은 횟수·일수(서버 validateKeepRemaining 과 같은 기준)
  const keepMax = keepUnit === 'COUNT' ? Number(changeInfo?.remainingCount) || 0 : Number(changeInfo?.remainingDays) || 0;
  const autoKeep = needsKeep && changePerUnit > 0 ? Math.floor(leftoverAmount / changePerUnit) : 0;
  useEffect(() => {
    setValue('keepRemaining', needsKeep ? String(autoKeep) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsKeep, autoKeep]);
  const watchUsedPoint = watch('usedPoint');

  // 변경수강권 검색 패널 — 판매등록의 수강권 검색창과 동일 UI (헤더 토글 + 검색 + 종목/일일권 칩 + 초성 그룹)
  const [changeTicketPanelOpen, setChangeTicketPanelOpen] = useState(false);
  const [changeTicketSearch, setChangeTicketSearch] = useState('');
  const [changeClassTypeFilter, setChangeClassTypeFilter] = useState('ALL');
  const [changeSportTypeFilter, setChangeSportTypeFilter] = useState('ALL');
  const changeTicketOptions = watchTicketType === 'TICKET' ? availableTickets : availablePasses;
  // 종목 칩 목록 — 옵션에 실제로 존재하는 종목만 (판매등록과 동일)
  const changeSportTypes = useMemo(
    () => [...new Set(changeTicketOptions.flatMap(item => item.sportTypes || []))],
    [changeTicketOptions],
  );
  const filteredChangeTicketOptions = useMemo(() => {
    let filtered = [...changeTicketOptions];
    if (watchTicketType === 'TICKET' && changeClassTypeFilter !== 'ALL') {
      filtered = filtered.filter(item => item.classType === changeClassTypeFilter);
    }
    if (changeSportTypeFilter !== 'ALL') {
      filtered = filtered.filter(item => (item.sportTypes || []).includes(changeSportTypeFilter));
    }
    if (changeTicketSearch.trim()) {
      filtered = filtered.filter(item => matchKorean(item.label || '', changeTicketSearch.trim()));
    }
    return filtered.sort((a, b) => (a.label || '').localeCompare(b.label || '', 'ko'));
  }, [changeTicketOptions, watchTicketType, changeClassTypeFilter, changeSportTypeFilter, changeTicketSearch]);

  // 회원권 변경 탭의 포인트 사용. 이 모달은 loadTickets/getManager 와 동일하게 로그인 지점 기준으로 동작한다.
  // (실제 차감 지점은 백엔드가 대상 주문의 member_order.BRANCH_ID 로 결정한다)
  const pointUsage = usePointUsage({
    memberId: ticketData?.memberId,
    userType: 'member',
    branchId: user.branch?.branchId,
    token: user.token,
  });
  const isPointFieldVisible = refundType === 'change' && pointUsage.isPointUsageEnabled;
  // 미수금 결제 탭 포인트 사용 (사용자 결정: 사용 + 실결제액 기준 적립)
  const isOutstandingPointVisible = refundType === 'outstandingPayment' && pointUsage.isPointUsageEnabled;

  // 환불/판매취소 시 포인트 정산 미리보기 — 백엔드 reverseForOrderCancellation 과 동일 식.
  // 복구 = 주문의 사용 포인트, 회수 = min(이 주문 구매 적립분, 복구 후 잔액).
  const orderUsedPoint = Number(orderPointInfo?.usedPoint) || 0;
  const orderEarnedPoint = Number(orderPointInfo?.earnedPoint) || 0;
  const reclaimPoint = Math.min(orderEarnedPoint, pointUsage.memberPointBalance + orderUsedPoint);

  const handleRefundTypeChange = event => {
    setRefundType(event.target.value);
    // 변경/미수금 결제 탭이 usedPoint 폼 필드를 공유하므로 미확정 입력이 넘어가지 않게 초기화
    setValue('usedPoint', '');
  };

  // 수강권/이용권 목록 불러오기
  const loadTickets = async () => {
    try {
      const query = {
        page: 1,
        limit: 1000000,
        headBranchId: user.headBranch?.headBranchId,
        branchId: user.branch?.branchId,
      };
      // 판매가 > 실결제액 서버 사전필터는 두지 않는다 — 선택 가능 여부는 잔여가치 기준으로 목록 안에서 회색 처리한다.

      const qstring = QueryString.stringify(query);

      // 수강권 목록
      const ticketsData = await getAllTickets(qstring, user.token);
      setAvailableTickets(
        ticketsData?.content.map(ticket => ({
          value: ticket.id,
          label:
            ticket.ticketName +
            ' ' +
            (ticket.usageCount ? ticket.usageCount + '회' : '') +
            ' ' +
            ticket?.usagePeriodDisplay,
          regularPrice: ticket.regularPrice,
          usageCount: ticket.usageCount,
          classType: ticket.classType,
          finalPrice: ticket.finalPrice,
          // 판매등록 검색창과 동일한 종목 뱃지 (복수 종목 없으면 대표 종목 폴백)
          sportTypes: ticket.sportTypes?.length ? ticket.sportTypes : ticket.sportType ? [ticket.sportType] : [],
        })),
      );

      // 이용권 목록
      const passesData = await getAllPass(qstring, user.token);
      setAvailablePasses(
        // 구독 이용권으로는 변경 불가(서버도 거부) — 후보에서 뺀다.
        passesData?.content.filter(pass => !pass.subscriptionYn).map(pass => ({
          value: pass.coursePassId,
          label: pass.passName + ' ' + pass?.usagePeriodDisplay,
          price: pass.price,
          regularPrice: pass.regularPrice,
          finalPrice: pass.finalPrice,
          sportTypes: pass.sportTypes?.length ? pass.sportTypes : pass.sportType ? [pass.sportType] : [],
        })),
      );
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 새 수강권/이용권 선택 시 가격 자동 설정
  useEffect(() => {
    if (watchTicketType) {
      setValue('newTicketPrice', 0);
      setValue('newTicketDiscountAmount', 0);
      setValue('newTicketFinalPrice', 0);
      setValue('newTicketOutstandingPrice', 0);
      setValue('newUsageCount', 0);

      setTicketPrice(0);
      setTicketDiscountAmount(0);
      setTicketFinalPrice(0);
      setTicketOutstandingPrice(0);
      setUsageCount(0);
      setSelectedTicketType(null);
    }

    if (watchNewTicket) {
      const selectedList = watchTicketType === 'TICKET' ? availableTickets : availablePasses;
      const selectedItem = selectedList.find(item => item.value === watchNewTicket);

      if (selectedItem) {
        const newPrice = Number(selectedItem.regularPrice) || 0;
        const discountAmount = (selectedItem.regularPrice || 0) - (selectedItem.finalPrice || 0);
        const currentUsedPoint = Number(getValues('usedPoint')) || 0;

        const finalNewPrice = newPrice - discountAmount;
        // 추가결제 = 새 상품가 − 잔여가치(실결제 × 남은 비율) − 사용 포인트
        const additionalPayment = Math.max(0, finalNewPrice - changeCredit - currentUsedPoint);

        setValue('newTicketPrice', selectedItem.regularPrice || 0);
        setValue('newTicketDiscountAmount', discountAmount || 0);
        setValue('newTicketFinalPrice', additionalPayment || 0);
        setValue('newTicketOutstandingPrice', 0);
        setValue('newUsageCount', selectedItem.usageCount || 0);

        // 상태도 업데이트 (새로운 변수명과 연결)
        setTicketPrice(selectedItem.regularPrice || 0);
        setTicketDiscountAmount(discountAmount || 0);
        setTicketFinalPrice(additionalPayment || 0);
        setTicketOutstandingPrice(0);
        setUsageCount(selectedItem.usageCount || 0);
        setSelectedTicketType(selectedItem.classType || null);
      }
    }
  }, [watchNewTicket, watchTicketType, availableTickets, availablePasses, setValue]);

  // 추가 결제 금액 자동 계산
  useEffect(() => {
    if (refundType === 'change') {
      const newPrice = Number(getValues('newTicketPrice')) || 0;
      const discountAmount = Number(getValues('newTicketDiscountAmount')) || 0;
      const outstandingAmount = Number(getValues('newTicketOutstandingPrice')) || 0;
      // 판매등록과 동일 계약 — 프론트가 사용 포인트를 뺀 순결제액을 백엔드로 보낸다.
      const currentUsedPoint = Number(getValues('usedPoint')) || 0;

      const finalNewPrice = newPrice - discountAmount;
      // 추가결제 = 새 상품가 − 잔여가치(실결제 × 남은 비율) − 사용 포인트
      const additionalPayment = Math.max(0, finalNewPrice - changeCredit - currentUsedPoint);

      setValue('newTicketFinalPrice', additionalPayment.toString());
    }
  }, [
    watch('newTicketPrice'),
    watch('newTicketDiscountAmount'),
    watch('newTicketOutstandingPrice'),
    watch('paymentAmountNow'),
    watchUsedPoint,
    changeCredit,
    refundType,
    setValue,
  ]);

  // 환불 금액 자동 계산 함수
  const calculateRefundAmount = (useAmount, penaltyAmount, paymentAmt, outstandingAmt) => {
    const payment = Number(paymentAmt) || 0;
    const outstanding = Number(outstandingAmt) || 0;
    const useAmountNum = Number(useAmount) || 0;
    const penaltyAmountNum = Number(penaltyAmount) || 0;

    // const calculatedRefund = payment - (outstanding + useAmountNum + penaltyAmountNum);
    const calculatedRefund = payment - (useAmountNum + penaltyAmountNum);
    return Math.max(0, calculatedRefund);
  };

  // 이용금액이나 위약금이 변경될 때마다 환불금액 자동 계산
  useEffect(() => {
    if (refundType === 'refund') {
      const calculatedRefund = calculateRefundAmount(
        watchUseAmount,
        watchPenaltyAmount,
        watchPaymentAmount,
        watchOutstandingAmount,
      );
      setValue('refundAmount', calculatedRefund.toString());
    }
  }, [watchUseAmount, watchPenaltyAmount, watchPaymentAmount, watchOutstandingAmount, refundType, setValue]);

  // props로 받은 값들을 폼에 설정 (모달이 열릴 때)
  useEffect(() => {
    if (open && ticketData) {
      // 기존 환불 관련 값들
      if (ticketData.finalPrice !== null && ticketData.finalPrice !== undefined) {
        setValue('paymentAmount', ticketData.finalPrice.toString());
      }
      if (ticketData.outstandingAmount !== null && ticketData.outstandingAmount !== undefined) {
        setValue('outstandingAmount', ticketData.outstandingAmount.toString());
      }

      // 변경 화면용 현재 정보 자동 입력
      setValue('courseTicketNameNow', ticketData.courseTicketName || '');
      setValue('usedCountNow', ticketData.usedCount?.toString() || '0');
      setValue('paymentAmountNow', ticketData.totalOrderPaymentAmount?.toString() || '');

      // 미수금 결제 날짜/시간 초기값 — 지금 시각 (사용자 결정. 수납은 보통 '지금' 발생한다)
      const nowMoment = moment();
      setValue('outstandingPaymentDate', nowMoment.format('YYYY-MM-DD'));
      setValue('outstandingPaymentTime', nowMoment.format('HH:mm'));

      // 환불일 기본값 = 오늘 (매출 이원화: 환불 차감은 환불일 기준으로 집계)
      setValue('refundDate', new Date());

      // 양수금 행은 환불/변경이 숨겨지므로 보이는 옵션(미수금 결제)을 기본 선택한다.
      setRefundType(ticketData.payType === 'TRANSFER_FEE' ? 'outstandingPayment' : 'refund');

      // 수강권/이용권 목록 로드
      loadTickets();
    }
  }, [open, ticketData, setValue]);

  const handleClose = (e, r) => {
    if (r === 'backdropClick') {
      return; // 백드롭 클릭으로 닫히는 것을 방지
    }

    reset();
    setRefundType('refund');
    setFormValues(null);
    setChangeTicketPanelOpen(false);
    setChangeTicketSearch('');
    setChangeClassTypeFilter('ALL');
    setChangeSportTypeFilter('ALL');
    onClose();
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    //setRefundType('refund');
    // setFormValues(null);
  };

  // 이용금액/위약금 변경 시 콜백 함수
  const handleUseAmountChange = value => {
    const calculatedRefund = calculateRefundAmount(
      value,
      getValues('penaltyAmount'),
      getValues('paymentAmount'),
      getValues('outstandingAmount'),
    );
    setValue('refundAmount', calculatedRefund.toString());
  };

  const handlePenaltyAmountChange = value => {
    const calculatedRefund = calculateRefundAmount(
      getValues('useAmount'),
      value,
      getValues('paymentAmount'),
      getValues('outstandingAmount'),
    );
    setValue('refundAmount', calculatedRefund.toString());
  };

  const submit = async values => {
    if (!memberOrderId) {
      return setError('searchInput', {
        type: 'custom',
      });
    }

    // 환불 타입별 검증
    // 0원 결제(전액 할인·포인트 전액 사용) 주문은 환불금액 0원도 유효하다.
    const zeroPaymentRefund =
      refundType === 'refund' && Number(String(values.paymentAmount || 0).replace(/,/g, '')) === 0;
    if (refundType === 'refund' && !zeroPaymentRefund
        && (!values.refundAmount || values.refundAmount <= 0)) {
      toast.error('환불 금액을 입력해주세요');
      return setError('refundAmount', { type: 'custom' });
    }

    // #49 — 환불수단 라인 합계 = 환불금액 (서버도 동일 검증)
    if (refundType === 'refund') {
      if (refundLines.length === 0) {
        toast.error('환불수단을 추가해주세요');
        return;
      }
      if (refundLinesTotal !== Number(values.refundAmount)) {
        toast.error(
          `환불수단 합계(${refundLinesTotal.toLocaleString()}원)가 환불금액(${Number(values.refundAmount).toLocaleString()}원)과 일치하지 않습니다`,
        );
        return;
      }
    }

    if (refundType === 'change') {
      if (changeBlockedByOutstanding) {
        toast.error('미수금이 남아있는 주문은 변경할 수 없습니다. 미수금을 먼저 정리해주세요.');
        return;
      }
      if (!values.ticketType) {
        toast.error('수강권 구분을 선택해주세요');
        return setError('ticketType', { type: 'custom' });
      }
      if (!values.newTicket) {
        toast.error('변경 수강권/이용권을 선택해주세요');
        return setError('newTicket', { type: 'custom' });
      }
      // 0 은 "남기지 않음"(1회·1일 금액보다 작은 자투리는 버린다 — 사용자 결정). 빈칸만 막는다.
      if (needsKeep && String(values.keepRemaining ?? '').replace(/,/g, '').trim() === '') {
        toast.error(keepUnit === 'COUNT' ? '기존 수강권에 남길 횟수를 입력해주세요(0 가능)' : '기존 이용권에 남길 일수를 입력해주세요(0 가능)');
        return setError('keepRemaining', { type: 'custom' });
      }
      if (needsKeep && Number(String(values.keepRemaining || '').replace(/,/g, '')) > keepMax) {
        toast.error(`남길 ${keepUnit === 'COUNT' ? '횟수' : '일수'}는 최대 ${keepMax}${keepUnit === 'COUNT' ? '회' : '일'}입니다`);
        return setError('keepRemaining', { type: 'custom' });
      }
      if (isPointFieldVisible) {
        // 포인트 적용 전 추가결제액이 사용 가능 상한이다.
        const paymentBeforePoint = Math.max(
          0,
          (Number(values.newTicketPrice) || 0) -
            (Number(values.newTicketDiscountAmount) || 0) -
            changeCredit,
        );
        const usedPointError = pointUsage.validateUsedPoint(values.usedPoint, paymentBeforePoint);
        if (usedPointError) {
          toast.error(usedPointError);
          return setError('usedPoint', { type: 'custom' });
        }
      }
    }

    if (refundType === 'outstandingPayment') {
      const cashPart = Number(String(values.outstandingPaymentAmount || 0).replace(/,/g, '')) || 0;
      const pointPart = isOutstandingPointVisible ? Number(values.usedPoint) || 0 : 0;
      const outstandingBalance = Number(ticketData?.outstandingAmount) || 0;
      if (cashPart + pointPart <= 0) {
        toast.error('미수금 결제금액을 입력해주세요');
        return setError('outstandingPaymentAmount', { type: 'custom' });
      }
      if (cashPart + pointPart > outstandingBalance) {
        toast.error(`수납액이 미수금 잔액(${outstandingBalance.toLocaleString()}원)을 초과합니다`);
        return setError('outstandingPaymentAmount', { type: 'custom' });
      }
      if (pointPart > 0) {
        const usedPointError = pointUsage.validateUsedPoint(values.usedPoint, outstandingBalance);
        if (usedPointError) {
          toast.error(usedPointError);
          return setError('usedPoint', { type: 'custom' });
        }
      }
    }

    setFormValues(values);
    if (refundType === 'change' || refundType === 'outstandingPayment') {
      // #122 새 주문 판매담당자 — 판매등록과 같은 규칙(1명 이상, N명이면 비율 합 100)
      if (!values.newOrderSellers?.length) {
        toast.error('판매 담당자를 선택해주세요');
        return;
      }
      if (!isSellerShareValid(values.newOrderSellers, values.newOrderShares)) {
        toast.error('판매 담당자 귀속 비율의 합은 100% 여야 합니다');
        return;
      }
      // 판매등록과 동일 — 수단(분할 라인)은 결제 정보 입력 모달에서 받는다.
      setPaySplitLines([]);
      setPayModalOpen(true);
      return;
    }
    setOpenDialog(true);
  };

  // 결제 정보 입력 모달 요약 — 변경은 추가결제, 미수금은 이번 수납 기준.
  const payModalSummary = (() => {
    if (!formValues) return null;
    const num = v => Number(String(v ?? 0).replace(/,/g, '')) || 0;
    if (refundType === 'change') {
      const usedPoint = isPointFieldVisible ? num(formValues.usedPoint) : 0;
      return {
        totalCount: 1,
        totalPrice: num(formValues.newTicketPrice),
        totalDiscount: num(formValues.newTicketDiscountAmount),
        totalOutstanding: num(formValues.newTicketOutstandingPrice),
        totalUsedPoint: usedPoint,
        totalFinalPrice: num(formValues.newTicketFinalPrice) + usedPoint,
      };
    }
    const balance = Number(ticketData?.outstandingAmount) || 0;
    const cashPart = num(formValues.outstandingPaymentAmount);
    const usedPoint = isOutstandingPointVisible ? num(formValues.usedPoint) : 0;
    return {
      totalCount: 1,
      totalPrice: balance,
      totalDiscount: 0,
      totalOutstanding: Math.max(0, balance - cashPart - usedPoint),
      totalUsedPoint: usedPoint,
      totalFinalPrice: cashPart + usedPoint,
    };
  })();

  // 라인 → 평면 multipart 파라미터(JSON 문자열)와 대표 수단(단일=한글 라벨, 복합='MIXED')
  const buildPayLines = () =>
    JSON.stringify(
      paySplitLines.map(line => ({
        method: line.method,
        amount: line.amount,
        ...(line.method === 'CARD' && {
          installmentMonth: line.installmentMonth || null,
          cardCompanyCode: line.cardCompanyCode || null,
          cardNumber: line.cardNumber || null,
          cid: line.cid || null, // Jackson 은 cId 필드를 'cid' 키로 받는다
        }),
        ...(line.method === 'TRANSFER' && {
          bankInfo: line.bankInfo || null,
          depositorName: line.depositorName || null,
        }),
      })),
    );
  const payLinesRepresentative = () => {
    const methods = [...new Set(paySplitLines.map(line => line.method))];
    return methods.length === 1 ? SPLIT_METHOD_LABEL[methods[0]] : 'MIXED';
  };

  // 이중 제출 방지 — 확인 더블클릭이 환불 라인을 중복 기록한 사고(198464) 재발 방지.
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const handleConfirmSubmit = async () => {
    if (confirmSubmitting) {
      return;
    }
    setConfirmSubmitting(true);
    try {
      const request = {
        memberOrderId: memberOrderId,
        refundType: refundType,
        refundManagerId: formValues.branchManager || null,
        memo: formValues.memo,
        // #122 새 주문 판매담당자 — 멀티파트라 배열이 List 로 안 가므로 CSV. 서버 resolveCsv 가 해석, 없으면 refundManagerId 1명 100%
        ...((refundType === 'change' || refundType === 'outstandingPayment') &&
          (() => {
            const seller = buildSellerPayload(formValues.newOrderSellers, formValues.newOrderShares);
            return seller.registeredBy
              ? {
                  refundManagerId: seller.registeredBy,
                  registeredByIds: seller.registeredByIds.join(','),
                  shareRates: seller.shareRates.join(','),
                }
              : {};
          })()),

        // 환불 관련
        ...(refundType === 'refund' && {
          ...(formValues.refundDate && {
            refundDate: moment(formValues.refundDate).format('YYYY-MM-DD'),
          }),
          refundAmount: Number(formValues.refundAmount),
          useAmount: Number(formValues.useAmount),
          penaltyAmount: Number(formValues.penaltyAmount),
          // #49 — 수단·상세는 라인이 진실. 서버가 대표 수단(단일=그 수단, 복합=MIXED)을 정한다.
          // 환불 API 는 평면 multipart 라 JSON 문자열 1개로 보낸다.
          refundPayments: JSON.stringify(
            refundLines.map(line => ({
              method: line.method,
              amount: line.amount,
              orderPaymentId: line.orderPaymentId || null,
              ...(line.method === 'CARD' && {
                installmentMonth: line.installmentMonth || null,
                cardCompanyCode: line.cardCompanyCode || null,
                cardNumber: line.cardNumber || null,
                cid: line.cid || null, // Jackson 은 cId 필드를 'cid' 키로 받는다 ('cId' 키는 매핑 안 됨)
              }),
              ...(line.method === 'TRANSFER' && {
                bankInfo: line.bankInfo || null,
                depositorName: line.depositorName || null,
              }),
              // 현금·계좌이체 환불 승인번호(현금영수증 취소) — 주문 레벨 입력을 라인에도 싣는다(판매등록과 동일)
              ...((line.method === 'CASH' || line.method === 'TRANSFER') && {
                cid: formValues.approvalNumber || null,
              }),
            })),
          ),
          // 현금·계좌이체 환불 라인이 있으면 현금영수증(취소) 정보를 환불 레벨로 보낸다.
          // 서버 취소환불 경로(normalizeRefundParams)는 refund* 접두 키를 읽는다 — receiptYn 으로 보내면 버려진다.
          ...(refundLines.some(line => line.method === 'CASH' || line.method === 'TRANSFER') && {
            ...(formValues.receiptYn === 'Y' && {
              refundDeductionType: formValues.deductionType === 0 ? 'PERSONAL' : 'BUSINESS',
              refundReceiptValue:
                formValues.deductionType === 0 ? formValues.issuePhoneNumber : formValues.issueBusinessNumber || null,
              refundReceiptType: formValues.deductionType === 0 ? formValues.receiptType : 'BUSINESS',
            }),
            refundReceiptYn: formValues.receiptYn || 'N',
            refundCId: formValues.approvalNumber || null,
          }),
          refundMethod: formValues.refundMethod || null,
          bankInfo: formValues.bankInfo || null,
          // ManualModalV2와 동일한 결제수단 관련 데이터 추가
          ...(formValues.refundMethod === '카드' && {
            installmentMonth: formValues.installmentMonth || null,
            cardCompanyCode: formValues.cardCompanyCode || null,
            cardNumber: formValues.cardNumber || null,
            cId: formValues.approvalNumber || null,
          }),
          ...(formValues.refundMethod === '계좌이체' && {
            ...(formValues.receiptYn === 'Y' && {
              deductionType: formValues.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
              receiptValue:
                formValues.deductionType === 0 ? formValues.issuePhoneNumber : formValues.issueBusinessNumber || null,
              receiptType: formValues.deductionType === 0 ? formValues.receiptType : 'BUSINESS' || null,
            }),
            receiptYn: formValues.receiptYn || null,
            depositorName: formValues.depositorName || null,
          }),
          ...(formValues.refundMethod === '현금' && {
            ...(formValues.receiptYn === 'Y' && {
              deductionType: formValues.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
              receiptValue:
                formValues.deductionType === 0 ? formValues.issuePhoneNumber : formValues.issueBusinessNumber || null,
              receiptType: formValues.deductionType === 0 ? formValues.receiptType : 'BUSINESS' || null,
            }),
            receiptYn: formValues.receiptYn || null,
          }),
        }),

        // 변경 관련 — 수단·상세는 결제 정보 입력 모달의 분할 라인(payments)이 진실.
        ...(refundType === 'change' && {
          newTicketType: formValues.ticketType,
          newTicketId: formValues.newTicket,
          changePaymentMethod: payLinesRepresentative(),
          payments: buildPayLines(),
          newTicketPrice: Number(formValues.newTicketPrice) - Number(formValues.paymentAmountNow),
          newTicketDiscountAmount: Number(formValues.newTicketDiscountAmount || 0),
          newTicketFinalPrice: Number(formValues.newTicketFinalPrice || 0),
          newTicketOutstandingPrice: Number(formValues.newTicketOutstandingPrice || 0),
          newUsageCount: Number(formValues.newUsageCount || 0),
          // 남는 금액을 기존 권으로 남길 때만 보낸다(서버가 종류·상한을 다시 검증).
          ...(needsKeep && {
            [keepUnit === 'COUNT' ? 'keepRemainingCount' : 'keepRemainingDays']: Number(
              String(formValues.keepRemaining || '').replace(/,/g, ''),
            ),
          }),
          usedPoint: isPointFieldVisible ? Number(formValues.usedPoint) || 0 : 0,
          receiptYn: formValues.receiptYn || null,
        }),

        // 미수금 결제 관련 — 수단·상세는 결제 정보 입력 모달의 분할 라인(payments)이 진실.
        ...(refundType === 'outstandingPayment' && {
          ...(formValues.outstandingPaymentDate && {
            paymentDate: `${moment(formValues.outstandingPaymentDate).format('YYYY-MM-DD')} ${formValues.outstandingPaymentTime || '00:00'}:00`,
          }),
          outstandingPaymentAmount: Number(String(formValues.outstandingPaymentAmount || 0).replace(/,/g, '')) || 0,
          usedPoint: isOutstandingPointVisible ? Number(formValues.usedPoint) || 0 : 0,
          refundMethod: payLinesRepresentative(),
          payments: buildPayLines(),
          ...(formValues.receiptYn === 'Y' && {
            deductionType: formValues.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            receiptValue:
              formValues.deductionType === 0 ? formValues.issuePhoneNumber : formValues.issueBusinessNumber || null,
            receiptType: formValues.deductionType === 0 ? formValues.receiptType : 'BUSINESS' || null,
          }),
          receiptYn: formValues.receiptYn || null,
        }),

        // 판매 취소 관련
        ...(refundType === 'cancel' && {
          refundAmount: 0,
          useAmount: 0,
          penaltyAmount: 0,
        }),

        // 추가 데이터들
        courseTicketName: courseTicketName,
        ticketId: ticketId,
        usedCount: usedCount,
      };

      console.log('판매 취소/환불 request:', request);

      await createProductCancel(request, user.token);
      toast.success('성공적으로 처리되었습니다');
      handleDialogClose();
      handleClose();
      console.log('fetchData 호출 시작');
      if (fetchData) {
        await fetchData();
        console.log('fetchData 호출 완료');
      } else {
        console.log('fetchData가 없습니다');
      }
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const getManager = async branch => {
    try {
      const data = await getBranchManager(branch, user.token);
      const branchManagerArray = data.map(item => ({
        value: item.managerId,
        label: item.managerName,
      }));
      setBranchManager(branchManagerArray);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // Redux에서 가져온 user.headBranch.headBranchId를 사용
  useEffect(() => {
    if (user?.headBranch?.headBranchId) {
      const branchId = user?.branch?.branchId || null;
      if (branchId) {
        setSelectedBranch(branchId);
      }
    }
  }, [user?.headBranch?.headBranchId, user?.branch?.branchId]);

  useEffect(() => {
    if (selectedBranch) {
      getManager(selectedBranch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  return (
    <div style={{ display: 'flex' }}>
      <Modal
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown>
        <ModalContainer>
          <ModalHeader>
            <ModalHeaderTitle>결제 수정</ModalHeaderTitle>
            <CloseIconWrapper onClick={handleClose}>
              <Close sx={{ width: '20px', height: '20px' }} />
            </CloseIconWrapper>
          </ModalHeader>

          <ModalContent>
                {/* 구분 선택 */}
                <TabContainer>
                  {!isTransferFee && (
                    <TabButton
                      active={refundType === 'change'}
                      position="first"
                      onClick={() => !isChangeDisabled && setRefundType('change')}
                      disabled={isChangeDisabled}>
                      회원권 변경
                    </TabButton>
                  )}
                  {!isTransferFee && (
                    <TabButton
                      active={refundType === 'refund'}
                      onClick={() => setRefundType('refund')}>
                      환불
                    </TabButton>
                  )}
                  <TabButton
                    active={refundType === 'cancel'}
                    position={isTransferFee ? 'first' : undefined}
                    onClick={() => setRefundType('cancel')}>
                    판매취소
                  </TabButton>
                  <TabButton
                    active={refundType === 'outstandingPayment'}
                    position="last"
                    onClick={() => setRefundType('outstandingPayment')}>
                    미수금 결제
                  </TabButton>
                </TabContainer>

                {/* 변경 화면 */}
                {refundType === 'change' && (
                  <>
                    {/* 카드 1 — 현재 상품 정보 (판매등록처럼 관련 입력을 카드 배경으로 묶는다) */}
                    <ContentSection sx={{ marginBottom: '12px' }}>
                      {changeBlockedByOutstanding && (
                        <Box
                          sx={{
                            marginBottom: '16px',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            backgroundColor: '#FDECEA',
                            color: '#C0392B',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}>
                          미수금 {(Number(changeInfo?.outstandingAmount ?? ticketData?.outstandingAmount) || 0).toLocaleString()}원이
                          남아있어 변경할 수 없습니다. 미수금 결제 탭에서 먼저 정리해주세요.
                        </Box>
                      )}
                      {/* 담당자는 하단 공용 '판매 담당자' 카드에서 받는다 — 현재 권 이름은 길 수 있어 전폭 */}
                      <Box sx={{ marginBottom: '16px' }}>
                        <FormFieldLabel>현재 수강권/이용권 명</FormFieldLabel>
                        <FormInputText control={control} name="courseTicketNameNow" disabled={true} sx={{ ...disabledInputStyle, width: '100%' }} />
                      </Box>

                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>사용횟수</FormFieldLabel>
                          <FormInputText control={control} name="usedCountNow" disabled={true} sx={disabledInputStyle} />
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>정가</FormFieldLabel>
                          <FormInputText
                            control={control}
                            name="changeRegularDisplay"
                            disabled={true}
                            staticValue={(Number(changeInfo?.regularAmount) || Number(ticketData?.totalOrderPaymentAmount) || 0).toLocaleString()}
                            sx={disabledInputStyle}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ color: '#737373', fontSize: '13px' }}>
                                  원
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormFieldGroupHalf>
                      </FormFieldRow>

                      {/* 결제금액=실결제액. 회당(일당) 금액은 수기 수정 가능 — 잔여가치가 이 값으로 재계산된다. */}
                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>결제금액</FormFieldLabel>
                          <FormInputText
                            control={control}
                            name="changePaidDisplay"
                            disabled={true}
                            staticValue={(Number(changeInfo?.paymentAmount) || Number(ticketData?.totalOrderPaymentAmount) || 0).toLocaleString()}
                            sx={disabledInputStyle}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ color: '#737373', fontSize: '13px' }}>
                                  원
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>
                            {changeInfo?.calcBasis === 'PERIOD' ? '일당 금액' : '회당 금액'}
                          </FormFieldLabel>
                          <Controller
                            name="changePerUnitInput"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <TextField
                                size="small"
                                sx={textInputStyle}
                                value={formatPerUnitDisplay(value)}
                                onChange={e => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
                                disabled={changeTotalUnits <= 0}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end" sx={{ color: '#737373', fontSize: '13px' }}>
                                      원
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />
                        </FormFieldGroupHalf>
                      </FormFieldRow>

                      {/* 잔여가치 = 실결제액 − 회당 금액 × 사용횟수(하한 0, 수기 수정 가능). */}
                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          {(() => {
                            const usedText = changeInfo?.calcBasis === 'COUNT'
                              ? `사용 ${changeInfo.usedCount}회 차감 −${Number(changeUsedValue || 0).toLocaleString()}원`
                              : changeInfo?.calcBasis === 'PERIOD'
                                ? `사용 ${changeInfo.usedDays}일 차감 −${Number(changeUsedValue || 0).toLocaleString()}원`
                                : '';
                            return (
                              <FormFieldLabel
                                title={usedText || undefined}
                                sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                                잔여가치
                                {usedText && <PointMinUseHint>({usedText})</PointMinUseHint>}
                              </FormFieldLabel>
                            );
                          })()}
                          <FormInputComma
                            control={control}
                            name="changeCreditInput"
                            placeholder="잔여가치"
                            endAdornmentText="원"
                            sx={textInputStyle}
                          />
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf />
                      </FormFieldRow>
                    </ContentSection>

                    {/* 카드 2 — 변경할 상품 선택·금액 */}
                    <ContentSection sx={{ marginBottom: '12px' }}>
                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>{t('수강권 구분')}</FormFieldLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '44px' }}>
                          {ticketTypeRadioOptions.map(option => (
                            <Controller
                              key={option.value}
                              name="ticketType"
                              control={control}
                              render={({ field }) => (
                                <FormControlLabel
                                  sx={{ ml: 0, mr: 2, fontWeight: 400 }}
                                  control={
                                    <CustomRadioBox
                                      checked={field.value === option.value}
                                      onChange={() => {
                                        field.onChange(option.value);
                                        setValue('newTicket', null); // 수강권 구분 변경 시 선택된 수강권 초기화
                                        setChangeTicketPanelOpen(false);
                                        setChangeTicketSearch('');
                                        setChangeClassTypeFilter('ALL');
                                        setChangeSportTypeFilter('ALL');
                                      }}
                                    />
                                  }
                                  label={option.label}
                                />
                              )}
                            />
                          ))}
                        </Box>
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf />
                    </FormFieldRow>

                    {/* 변경수강권 선택 — 판매등록의 수강권 검색창과 동일 UI (전폭) */}
                    {watchTicketType && (
                      <Box sx={{ marginBottom: '16px' }}>
                        <FormFieldLabel>
                          {watchTicketType === 'TICKET' ? t('변경수강권') : t('변경이용권')}
                          <PointMinUseHint title="잔여가치 = 실결제액 × 남은 비율(실결제 단가 기준). 판매가가 잔여가치보다 낮으면 남는 금액을 기존 수강권 횟수·이용권 일수로 남깁니다.">
                            {keepUnit
                              ? `(잔여가치 ${changeCreditGate.toLocaleString()}원 · 더 싼 상품은 남는 금액을 기존 권 ${keepUnit === 'COUNT' ? '횟수' : '일수'}로 남김)`
                              : `(판매가 ${changeCreditGate.toLocaleString()}원 이상 상품만 선택 가능 · 잔여가치 = 실결제액 × 남은 비율)`}
                          </PointMinUseHint>
                        </FormFieldLabel>
                        <Box sx={{ width: '100%' }}>
                          {/* 헤더 (클릭하면 펼침/접힘) */}
                          <Box
                            onClick={() => { setChangeTicketPanelOpen(prev => !prev); setChangeTicketSearch(''); }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              height: '44px',
                              px: '14px',
                              border: changeTicketPanelOpen ? '1.5px solid #0F7F85' : '1px solid #a6a6a6',
                              borderRadius: changeTicketPanelOpen ? '6px 6px 0 0' : '6px',
                              cursor: 'pointer',
                              backgroundColor: watchNewTicket ? '#f0f8f8' : '#fff',
                              transition: 'all 0.15s ease',
                              '&:hover': { borderColor: '#0F7F85' },
                            }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: watchNewTicket ? 500 : 400, color: watchNewTicket ? '#0F7F85' : '#999' }}>
                              {watchNewTicket
                                ? changeTicketOptions.find(item => item.value === watchNewTicket)?.label || '선택됨'
                                : watchTicketType === 'TICKET'
                                  ? t('수강권을 선택해주세요')
                                  : t('이용권을 선택해주세요')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: changeTicketPanelOpen ? '#0F7F85' : 'transparent', transition: 'all 0.15s ease' }}>
                              {changeTicketPanelOpen ? <KeyboardArrowUpIcon sx={{ color: '#fff', fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />}
                            </Box>
                          </Box>

                          {/* 패널 (펼쳐졌을 때) */}
                          {changeTicketPanelOpen && (
                            <Box sx={{ border: '1.5px solid #0F7F85', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '360px', display: 'flex', flexDirection: 'column' }}>
                              {/* 검색 + 클래스 필터 (이용권은 구분이 없어 검색만) */}
                              <Box sx={{ display: 'flex', gap: '8px', p: '10px 12px', borderBottom: '1px solid #eee' }}>
                                {watchTicketType === 'TICKET' && (
                                  <Select
                                    value={changeClassTypeFilter}
                                    onChange={e => setChangeClassTypeFilter(e.target.value)}
                                    size="small"
                                    sx={{ minWidth: '100px', fontSize: '13px', '& .MuiSelect-select': { py: '6px' } }}>
                                    <MenuItem value="ALL">{t('전체')}</MenuItem>
                                    <MenuItem value="PRIVATE_LESSON">{t('개인')}</MenuItem>
                                    <MenuItem value="GROUP_LESSON">{t('그룹')}</MenuItem>
                                  </Select>
                                )}
                                <TextField
                                  size="small"
                                  placeholder={watchTicketType === 'TICKET' ? '수강권 검색' : '이용권 검색'}
                                  value={changeTicketSearch}
                                  onChange={e => setChangeTicketSearch(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  autoFocus
                                  sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
                                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment> }}
                                />
                              </Box>

                              {/* 종목 필터 — 판매등록 검색창과 동일한 칩 (변경은 일일권 미지원이라 일일권 칩 없음, 사용자 결정) */}
                              {(
                                <Box sx={{ display: 'flex', gap: '6px', px: '12px', py: '8px', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
                                  {changeSportTypes.length > 0 && (
                                    <>
                                      <Box
                                        onClick={() => setChangeSportTypeFilter('ALL')}
                                        sx={{
                                          px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                          backgroundColor: changeSportTypeFilter === 'ALL' ? '#4ab3bc' : '#f5f5f5',
                                          color: changeSportTypeFilter === 'ALL' ? '#fff' : '#666',
                                          '&:hover': { opacity: 0.8 },
                                        }}>
                                        전체
                                      </Box>
                                      {changeSportTypes.map(type => (
                                        <Box
                                          key={type}
                                          onClick={() => setChangeSportTypeFilter(type)}
                                          sx={{
                                            px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                            backgroundColor: changeSportTypeFilter === type ? '#4ab3bc' : '#f5f5f5',
                                            color: changeSportTypeFilter === type ? '#fff' : '#666',
                                            '&:hover': { opacity: 0.8 },
                                          }}>
                                          {t(type)}
                                        </Box>
                                      ))}
                                    </>
                                  )}
                                </Box>
                              )}

                              {/* 목록 — 초성 그룹 헤더 */}
                              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                                {filteredChangeTicketOptions.length === 0 ? (
                                  <Box sx={{ p: '24px', textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '13px', color: '#999' }}>검색 결과가 없습니다</Typography>
                                  </Box>
                                ) : (
                                  (() => {
                                    let lastCho = '';
                                    return filteredChangeTicketOptions.map(item => {
                                      const firstChar = (item.label || '')[0] || '';
                                      const cho = getChosung(firstChar);
                                      const showHeader = cho !== lastCho;
                                      lastCho = cho;
                                      const isSelected = watchNewTicket === item.value;
                                      // 판매가(듀엣은 1인분)가 잔여가치 미만이면 선택 불가(표시는 유지, 사용자 결정).
                                      // 단 남는 금액을 기존 권 횟수·일수로 남길 수 있는 권(keepUnit)이면 고를 수 있다.
                                      const belowCredit = toPerMemberPrice(item) < changeCreditGate;
                                      const notEligible = belowCredit && !keepUnit;
                                      return (
                                        <React.Fragment key={item.value}>
                                          {showHeader && (
                                            <Box sx={{ px: '14px', py: '4px', backgroundColor: '#f7f7f7', borderBottom: '1px solid #eee' }}>
                                              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#999' }}>{cho}</Typography>
                                            </Box>
                                          )}
                                          <Box
                                            onClick={() => {
                                              if (notEligible) return;
                                              setValue('newTicket', item.value);
                                              setChangeTicketPanelOpen(false);
                                              setChangeTicketSearch('');
                                            }}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              px: '14px',
                                              py: '10px',
                                              cursor: notEligible ? 'not-allowed' : 'pointer',
                                              opacity: notEligible ? 0.45 : 1,
                                              backgroundColor: isSelected ? '#f0f8f8' : notEligible ? '#fafafa' : '#fff',
                                              borderBottom: '1px solid #f5f5f5',
                                              '&:hover': { backgroundColor: isSelected ? '#e6f5f5' : notEligible ? '#fafafa' : '#f5f5f5' },
                                              '&:last-child': { borderBottom: 'none' },
                                            }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                                              {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: '#0F7F85', flexShrink: 0 }} />}
                                              <Typography sx={{ fontSize: '13px', color: '#333', fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.label}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px', flexShrink: 0, ml: '8px' }}>
                                              <Box sx={{ display: 'flex', gap: '4px' }}>
                                                {belowCredit && (
                                                  <Typography sx={{ fontSize: '10px', fontWeight: 600, px: '6px', py: '1px', borderRadius: '3px', color: '#999', backgroundColor: '#f0f0f0', border: '1px solid #ddd' }}>
                                                    {notEligible ? '잔여가치 미만' : keepUnit === 'COUNT' ? '남는 금액 → 횟수' : '남는 금액 → 일수'}
                                                  </Typography>
                                                )}
                                                {item.classType && (
                                                  <Typography sx={{ fontSize: '10px', fontWeight: 600, px: '6px', py: '1px', borderRadius: '3px', color: '#e67e22', backgroundColor: '#fef5e7', border: '1px solid #f5d9a8' }}>
                                                    {item.classType === 'PRIVATE_LESSON' ? t('개인') : t('그룹')}
                                                  </Typography>
                                                )}
                                                {(item.sportTypes || []).map(st => (
                                                  <Typography key={st} sx={{ fontSize: '10px', fontWeight: 600, px: '6px', py: '1px', borderRadius: '3px', color: '#4ab3bc', backgroundColor: '#edf8f9', border: '1px solid #d0edf0' }}>
                                                    {t(st)}
                                                  </Typography>
                                                ))}
                                              </Box>
                                              {/* 판매등록 검색창과 동일 — 할인이 있으면 정가에 취소선을 긋고 판매가를 함께 보여준다. */}
                                              {Number(item.regularPrice) > 0 && (
                                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                                  {item.finalPrice != null && Number(item.finalPrice) !== Number(item.regularPrice) && (
                                                    <Typography sx={{ fontSize: '11px', lineHeight: 1, color: '#bbb', textDecoration: 'line-through' }}>
                                                      {Number(item.regularPrice).toLocaleString()}원
                                                    </Typography>
                                                  )}
                                                  <Typography sx={{ fontSize: '12px', lineHeight: 1, fontWeight: 600, color: notEligible ? '#bbb' : '#0F7F85' }}>
                                                    {Number(item.finalPrice ?? item.regularPrice).toLocaleString()}원
                                                  </Typography>
                                                </Box>
                                              )}
                                            </Box>
                                          </Box>
                                        </React.Fragment>
                                      );
                                    });
                                  })()
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {needsKeep && (
                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>
                            {keepUnit === 'COUNT' ? '기존 수강권에 남길 횟수' : '기존 이용권에 남길 일수'}
                            <PointMinUseHint>
                              {`(남는 금액 ${leftoverAmount.toLocaleString()}원 · ${keepUnit === 'COUNT' ? '회당' : '일당'} ${Math.round(changePerUnit).toLocaleString()}원 → 자동 ${autoKeep}${keepUnit === 'COUNT' ? '회' : '일'} · 최대 ${keepMax}${keepUnit === 'COUNT' ? '회' : '일'} · 0이면 남기지 않음)`}
                            </PointMinUseHint>
                          </FormFieldLabel>
                          <FormInputComma
                            control={control}
                            name="keepRemaining"
                            placeholder="0"
                            endAdornmentText={keepUnit === 'COUNT' ? '회' : '일'}
                            sx={textInputStyle}
                          />
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf />
                      </FormFieldRow>
                    )}

                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>수강권 금액</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="newTicketPrice"
                          disabled={true}
                          endAdornmentText="원"
                          sx={disabledInputStyle}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>할인금액</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="newTicketDiscountAmount"
                          placeholder="할인금액"
                          endAdornmentText="원"
                          sx={textInputStyle}
                        />
                      </FormFieldGroupHalf>
                    </FormFieldRow>

                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>미수금</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="newTicketOutstandingPrice"
                          placeholder="미수금"
                          endAdornmentText="원"
                          sx={textInputStyle}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>이용횟수</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="newUsageCount"
                          placeholder="이용횟수"
                          endAdornmentText="회"
                          sx={textInputStyle}
                        />
                      </FormFieldGroupHalf>
                    </FormFieldRow>

                    {/* 포인트 사용 (정식 회원 + 지점 포인트 사용 설정 ON 일 때만) */}
                    {isPointFieldVisible && (
                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>보유 포인트</FormFieldLabel>
                          <FormInputText
                            control={control}
                            name="availablePointDisplay"
                            disabled={true}
                            staticValue={pointUsage.availablePoint.toLocaleString()}
                            sx={disabledInputStyle}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ color: '#737373', fontSize: '13px' }}>
                                  P
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf>
                          {(() => {
                            const base = Math.max(
                              0,
                              (Number(String(watch('newTicketPrice') || 0).replace(/,/g, '')) || 0) -
                                (Number(String(watch('newTicketDiscountAmount') || 0).replace(/,/g, '')) || 0) -
                                changeCredit,
                            );
                            const maxUsable = pointUsage.resolveMaxUsable(base);
                            const hintBody = [
                              pointUsage.minUsePoint > 0 && `최소 ${pointUsage.minUsePoint.toLocaleString()}P`,
                              Number.isFinite(maxUsable) && maxUsable > 0 && `최대 ${maxUsable.toLocaleString()}P`,
                            ]
                              .filter(Boolean)
                              .join(' · ');
                            const hintText = hintBody ? `(${hintBody})` : '';
                            return (
                              <FormFieldLabel
                                title={hintText || undefined}
                                sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                                사용 포인트
                                {hintText && <PointMinUseHint>{hintText}</PointMinUseHint>}
                              </FormFieldLabel>
                            );
                          })()}
                          <FormInputComma
                            control={control}
                            name="usedPoint"
                            placeholder="0"
                            endAdornmentText="P"
                            sx={textInputStyle}
                          />
                        </FormFieldGroupHalf>
                      </FormFieldRow>
                    )}

                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        {/* 자동 계산값이 기본으로 채워지고, 필요 시 직접 수정할 수 있다(수정 후 상단 값이 바뀌면 재계산됨). */}
                        <FormFieldLabel>추가결제 금액</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="newTicketFinalPrice"
                          placeholder="추가결제 금액"
                          endAdornmentText="원"
                          sx={textInputStyle}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf />
                    </FormFieldRow>
                    </ContentSection>

                    {/* 결제수단·상세는 '변경' 버튼을 누르면 뜨는 결제 정보 입력 모달에서 받는다(판매등록과 동일). */}
                  </>
                )}

                {/* 환불 화면 */}
                {refundType === 'refund' && (
                  <>
                    {/* 피그마(6518-9706) 기반. 환불일과 판매 담당자를 한 행으로(판매등록과 통일).
                        판매등록처럼 환불 정보 입력을 카드 배경으로 묶는다. */}
                    <ContentSection sx={{ marginBottom: '12px' }}>
                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>환불일</FormFieldLabel>
                        <FormInputDate
                          name="refundDate"
                          control={control}
                          maxDate={new Date()}
                          placeholder="YYYY-MM-DD"
                          sx={{ width: '100%' }}
                          renderInput={params => (
                            <TextField size="small" variant="outlined" {...params} sx={{ ...dateInputStyleFlex, width: '100%' }} />
                          )}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
                        <FormInputDropdown
                          control={control}
                          sx={{ ...dropdownStyle, width: '100%' }}
                          rules={{ required: true }}
                          name="branchManager"
                          options={branchManager}
                          disabledItem={<MenuItem disabled>{t('담당자 선택')}</MenuItem>}
                          IconComponent={DropdownArrowIcon}
                        />
                      </FormFieldGroupHalf>
                    </FormFieldRow>
                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>이용금액</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="useAmount"
                          onValueChange={handleUseAmountChange}
                          placeholder="이용금액"
                          endAdornmentText="원"
                          sx={textInputStyle}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>위약금</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="penaltyAmount"
                          onValueChange={handlePenaltyAmountChange}
                          placeholder="위약금"
                          endAdornmentText="원"
                          sx={textInputStyle}
                        />
                      </FormFieldGroupHalf>
                    </FormFieldRow>
                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>미수금</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="outstandingAmount"
                          disabled={true}
                          endAdornmentText="원"
                          sx={disabledInputStyle}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>환불금액</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="refundAmount"
                          disabled={true}
                          endAdornmentText="원"
                          sx={disabledInputStyle}
                        />
                      </FormFieldGroupHalf>
                    </FormFieldRow>
                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>결제금액</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="paymentAmount"
                          disabled={true}
                          endAdornmentText="원"
                          sx={disabledInputStyle}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf />
                    </FormFieldRow>
                    </ContentSection>

                    {/* #49 — 환불수단은 수납 라인 방식. 판매등록 결제 모달의 결제수단과 같은
                        ContentSection 카드를 그대로 재사용해 UI 를 통일한다. */}
                    <ContentSection sx={{ marginBottom: '12px' }}>
                      <SplitPaymentSection
                        plain
                        refundMode
                        totalAmount={watchRefundAmountValue}
                        lines={refundLines}
                        onChange={setRefundLines}
                        formatInteger={value => (value === 0 ? '0' : Number(value || 0).toLocaleString())}
                        cashReceipt={{
                          approvalNumber: watch('approvalNumber'),
                          receiptYn: watch('receiptYn'),
                          deductionType: watch('deductionType'),
                          phoneNumber: watch('issuePhoneNumber'),
                          businessNumber: watch('issueBusinessNumber'),
                        }}
                        // 현금·계좌이체 환불이면 현금영수증(취소 승인번호) 폼 — 판매등록·판매정보수정과 같은 자리
                        beforeAddButton={currentMethod =>
                          (currentMethod === 'CASH' ||
                            currentMethod === 'TRANSFER' ||
                            refundLines.some(line => line.method === 'CASH' || line.method === 'TRANSFER')) && (
                            <Box style={{ margin: '16px 0 4px' }}>
                              <CashPaymentForm control={control} setValue={setValue} />
                            </Box>
                          )
                        }
                      />
                    </ContentSection>

                  </>
                )}

                {/* 판매취소 화면 — 되돌려줄 금액·포인트 안내 (판매등록처럼 카드 배경으로) */}
                {refundType === 'cancel' && (
                  <ContentSection sx={{ marginBottom: '12px' }}>
                  <FormFieldRow>
                    <FormFieldGroupHalf>
                      <FormFieldLabel>반환 금액</FormFieldLabel>
                      <FormInputText
                        control={control}
                        name="cancelReturnAmountDisplay"
                        disabled={true}
                        staticValue={(Number(ticketData?.finalPrice) || 0).toLocaleString()}
                        sx={disabledInputStyle}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ color: '#737373', fontSize: '13px' }}>
                              원
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormFieldGroupHalf>
                    <FormFieldGroupHalf>
                      <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
                      <FormInputDropdown
                        control={control}
                        sx={{ ...dropdownStyle, width: '100%' }}
                        rules={{ required: true }}
                        name="branchManager"
                        options={branchManager}
                        disabledItem={<MenuItem disabled>{t('담당자 선택')}</MenuItem>}
                        IconComponent={DropdownArrowIcon}
                      />
                    </FormFieldGroupHalf>
                  </FormFieldRow>
                  </ContentSection>
                )}

                {/* 미수금 결제 화면 — 결제일시 전폭, [미수금|남은 미수금], 포인트, [미수금 결제금액] (판매등록처럼 카드 배경) */}
                {refundType === 'outstandingPayment' && (
                  <ContentSection sx={{ marginBottom: '12px' }}>
                    {/* 결제일시 + 판매 담당자 — 판매등록의 판매일시 옆 담당자 배치와 통일 */}
                    <Box sx={{ marginBottom: '16px', display: 'flex', gap: '16px' }}>
                      <Box sx={{ flex: 1.8, minWidth: 0 }}>
                        <FormFieldLabel>결제일시</FormFieldLabel>
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', minWidth: 0 }}>
                          <FormInputDate
                            name="outstandingPaymentDate"
                            control={control}
                            placeholder="YYYY-MM-DD"
                            sx={{ flex: 1, minWidth: 0 }}
                            renderInput={params => (
                              <TextField size="small" variant="outlined" {...params} sx={{ ...dateInputStyleFlex, width: '100%' }} />
                            )}
                          />
                          <CustomTimePicker
                            value={watch('outstandingPaymentTime')}
                            onChange={val => setValue('outstandingPaymentTime', val)}
                            sx={{ flexShrink: 0 }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
                        {/* #122 미수금 수납 주문도 담당자 N명 + 비율 — 판매등록과 같은 부품 */}
                        <SellerShareSelect
                          control={control}
                          setValue={setValue}
                          name="newOrderSellers"
                          sharesName="newOrderShares"
                          options={branchManager || []}
                          placeholder={t('담당자 선택')}
                        />
                      </Box>
                    </Box>

                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>미수금</FormFieldLabel>
                        <FormInputText
                          control={control}
                          name="outstandingBalanceDisplay"
                          disabled={true}
                          staticValue={(Number(ticketData?.outstandingAmount) || 0).toLocaleString()}
                          sx={disabledInputStyle}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ color: '#737373', fontSize: '13px' }}>
                                원
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>남은 미수금</FormFieldLabel>
                        <FormInputText
                          control={control}
                          name="outstandingRemainDisplay"
                          disabled={true}
                          staticValue={(() => {
                            const balance = Number(ticketData?.outstandingAmount) || 0;
                            const cash = Number(String(watch('outstandingPaymentAmount') || 0).replace(/,/g, '')) || 0;
                            const point = isOutstandingPointVisible ? Number(watch('usedPoint')) || 0 : 0;
                            return Math.max(0, balance - cash - point).toLocaleString();
                          })()}
                          sx={disabledInputStyle}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ color: '#737373', fontSize: '13px' }}>
                                원
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormFieldGroupHalf>
                    </FormFieldRow>

                    {/* 포인트 사용 — 판매등록과 동일 규칙 (실결제+포인트 ≤ 미수금 잔액) */}
                    {isOutstandingPointVisible && (
                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>보유 포인트</FormFieldLabel>
                          <FormInputText
                            control={control}
                            name="availablePointDisplay"
                            disabled={true}
                            staticValue={pointUsage.availablePoint.toLocaleString()}
                            sx={disabledInputStyle}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ color: '#737373', fontSize: '13px' }}>
                                  P
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf>
                          {(() => {
                            const outstandingBase = Number(ticketData?.outstandingAmount) || 0;
                            const maxUsable = pointUsage.resolveMaxUsable(outstandingBase);
                            const hintBody = [
                              pointUsage.minUsePoint > 0 && `최소 ${pointUsage.minUsePoint.toLocaleString()}P`,
                              Number.isFinite(maxUsable) && maxUsable > 0 && `최대 ${maxUsable.toLocaleString()}P`,
                            ]
                              .filter(Boolean)
                              .join(' · ');
                            const hintText = hintBody ? `(${hintBody})` : '';
                            return (
                              <FormFieldLabel
                                title={hintText || undefined}
                                sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                                사용 포인트
                                {hintText && <PointMinUseHint>{hintText}</PointMinUseHint>}
                              </FormFieldLabel>
                            );
                          })()}
                          <FormInputComma
                            control={control}
                            name="usedPoint"
                            placeholder="0"
                            endAdornmentText="P"
                            sx={textInputStyle}
                          />
                        </FormFieldGroupHalf>
                      </FormFieldRow>
                    )}

                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>미수금 결제금액</FormFieldLabel>
                        <FormInputComma
                          control={control}
                          name="outstandingPaymentAmount"
                          placeholder="미수금 결제금액"
                          endAdornmentText="원"
                          sx={textInputStyle}
                        />
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf />
                    </FormFieldRow>

                    {/* 결제수단·상세는 '결제' 버튼을 누르면 뜨는 결제 정보 입력 모달에서 받는다(판매등록과 동일). */}
                  </ContentSection>
                )}

                {/* 환불/판매취소 포인트 정산 안내 — 백엔드 reverseForOrderCancellation 과 동일 식. 맨 아래 고정. */}
                {(refundType === 'refund' || refundType === 'cancel') &&
                  (orderUsedPoint > 0 || orderEarnedPoint > 0) && (
                    <Box sx={pointPreviewBoxStyle}>
                      <Typography sx={pointPreviewTitleStyle}>
                        {refundType === 'cancel' ? '취소 시 포인트 정산' : '환불 시 포인트 정산'}
                      </Typography>
                      {orderUsedPoint > 0 && (
                        <Typography sx={pointPreviewRowStyle}>
                          결제에 사용한 포인트 복구 <b>+{orderUsedPoint.toLocaleString()}P</b>
                        </Typography>
                      )}
                      {orderEarnedPoint > 0 && (
                        <Typography sx={pointPreviewRowStyle}>
                          구매 적립 포인트 회수 <b>-{reclaimPoint.toLocaleString()}P</b>
                          {reclaimPoint < orderEarnedPoint &&
                            ` (적립 ${orderEarnedPoint.toLocaleString()}P 중 잔액 한도)`}
                        </Typography>
                      )}
                      <Typography sx={{ ...pointPreviewRowStyle, marginTop: '4px', color: '#444' }}>
                        현재 보유 <b>{pointUsage.memberPointBalance.toLocaleString()}P</b> → 정산 후{' '}
                        <b>
                          {(pointUsage.memberPointBalance + orderUsedPoint - reclaimPoint).toLocaleString()}P
                        </b>
                      </Typography>
                    </Box>
                  )}

                {/* 판매 담당자 — 환불·미수금 결제 탭은 일시 필드 옆, 판매취소 탭은 반환 금액 옆으로 이동.
                    회원권 변경 탭만 기존 하단 카드 유지 */}
                {refundType === 'change' && (
                  <ContentSection sx={{ marginBottom: '12px' }}>
                    <FormFieldGroup>
                      <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
                      {/* #122 회원권 변경 새 주문도 담당자 N명 + 비율 — 판매등록과 같은 부품 */}
                      <SellerShareSelect
                        control={control}
                        setValue={setValue}
                        name="newOrderSellers"
                        sharesName="newOrderShares"
                        options={branchManager || []}
                        placeholder={t('담당자 선택')}
                      />
                    </FormFieldGroup>
                  </ContentSection>
                )}

                {/* 사유 — 피그마 환불사유 카드(라벨 상단 + 전폭 입력)를 4탭 공통으로 사용 */}
                <ContentSection sx={{ marginBottom: '12px' }}>
                  <FormFieldLabel>
                    {refundType === 'change' && '변경 신청 배경'}
                    {refundType === 'refund' && '환불사유'}
                    {refundType === 'cancel' && '판매취소 사유'}
                    {refundType === 'outstandingPayment' && '사유'}
                  </FormFieldLabel>
                  <FormInputTextArea
                    control={control}
                    minRows={4}
                    style={{
                      width: '100%',
                      height: '90px',
                      padding: '12px 13px 10px 11px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: '1px solid #a6a6a6',
                      borderRadius: '8px',
                      resize: 'none',
                    }}
                    placeholder={t('메모를 입력해 주세요.')}
                    name="memo"
                    rules={{ required: true }}
                  />
                </ContentSection>


          </ModalContent>

          <ModalFooter>
            <CancelButton onClick={handleClose}>{t('취소')}</CancelButton>
            {refundType === 'refund' || refundType === 'cancel' ? (
              <DangerOutlineButton type="button" onClick={handleSubmit(submit)}>
                {refundType === 'refund' ? '환불처리' : '취소처리'}
              </DangerOutlineButton>
            ) : (
              <SaveButton
                disabled={refundType === 'change' && changeBlockedByOutstanding}
                onClick={handleSubmit(submit)}>
                {/* 회원권변경·미수금결제 모두 결제 정보 입력 모달로 이어지므로 '다음' — 최종 확정은 모달의 '결제 완료' */}
                {'다음'}
              </SaveButton>
            )}
          </ModalFooter>
        </ModalContainer>
      </Modal>

      {/* 회원권 변경/미수금 결제 — 판매등록과 동일한 결제 정보 입력 모달(분할 라인) */}
      <PaymentConfirmModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        onConfirm={() => {
          setPayModalOpen(false);
          handleConfirmSubmit();
        }}
        control={control}
        setValue={setValue}
        cartSummary={payModalSummary}
        formatInteger={value => (value === 0 ? '0' : Number(value || 0).toLocaleString())}
        isSubmitting={false}
        splitLines={paySplitLines}
        setSplitLines={setPaySplitLines}
      />

      {/* 확인 다이얼로그 */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        PaperProps={{ sx: { borderRadius: '12px', minWidth: '300px' } }}>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#222', pb: '4px' }}>
          {refundType === 'change' && '변경 확인'}
          {refundType === 'refund' && '환불 확인'}
          {refundType === 'cancel' && '취소 확인'}
          {refundType === 'outstandingPayment' && '결제 확인'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '13px', color: '#666' }}>
            {refundType === 'change' && '회원권을 변경하시겠습니까?'}
            {refundType === 'refund' && '환불을 진행하시겠습니까?'}
            {refundType === 'cancel' && '판매를 취소하시겠습니까?'}
            {refundType === 'outstandingPayment' && '미수금 결제를 진행하시겠습니까?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: '16px', pb: '12px', gap: '6px' }}>
          <Button onClick={handleDialogClose} sx={{ color: '#666', fontWeight: 600, fontSize: '13px', borderRadius: '8px', textTransform: 'none' }}>
            취소
          </Button>
          <Button
            disabled={confirmSubmitting}
            onClick={handleConfirmSubmit} autoFocus variant="contained"
            sx={{ background: '#4AB3BC', fontWeight: 600, fontSize: '13px', borderRadius: '8px', px: '14px', textTransform: 'none', boxShadow: 'none', '&:hover': { background: '#3EA0A8', boxShadow: 'none' } }}>
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ManualRefundModalV2;
